import React, { useEffect, useMemo } from 'react';
import { flex } from '@fabric-ui/theme';
import { Box } from '@mui/material';
import { useRouter } from '@tanstack/react-router';
import { useAvCopilotContext } from '../../context/AvCopilotProvider';
import { NavigationBlockerModal } from '../../context/NavigationBlockerProvider';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';

interface ICopilotNavigationBlocker {
  isDirty: boolean;
  isManualDialogOpen: boolean;
  onManualClose: () => void;
  onManualDiscard: () => void;
}

const CopilotNavigationBlocker = ({ isDirty, isManualDialogOpen, onManualClose, onManualDiscard }: ICopilotNavigationBlocker) => {
  const { forceDiscardCopilot, messages, lastSessionProps } = useAvCopilotContext();
  const router = useRouter();

  const lastMessage = messages.at(-1);
  const dialogConfig = lastSessionProps.getConfirmDialogConfig?.(lastMessage);

  const modalContent = useMemo(
    () => ({
      title: dialogConfig?.title,
      body: (
        <Box sx={{ ...flex.col, gap: 2 }}>
          <div>{dialogConfig?.body}</div>
          <div>How would you like to proceed?</div>
        </Box>
      ),
      confirmText: 'Back To Chat',
      discardText: 'Discard Chat',
    }),
    [dialogConfig]
  );

  useEffect(() => {
    const unsubscribeOnBeforeNavigate = router.subscribe('onBeforeNavigate', e => {
      if (e.pathChanged && !isDirty) {
        forceDiscardCopilot();
      }
    });

    return () => {
      unsubscribeOnBeforeNavigate();
    };
  }, []);

  useNavigationBlocker({ isDirty, onDiscard: forceDiscardCopilot, modalContent });

  if (!isManualDialogOpen) {
    return null;
  }

  return <NavigationBlockerModal {...modalContent} open={isManualDialogOpen} onClose={onManualClose} onDiscard={onManualDiscard} />;
};

export default CopilotNavigationBlocker;
